import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import api, { apiErr } from "@/lib/api";
import { fmtMoney, fmtDate, formatWebinarTime } from "@/lib/format";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { BookOpen, CalendarClock, Receipt, Award, User, Play, Download } from "lucide-react";

const TABS = [
  { id: "courses", label: "My Courses", icon: BookOpen },
  { id: "webinars", label: "My Webinars", icon: CalendarClock },
  { id: "purchases", label: "My Purchases", icon: Receipt },
  { id: "certificates", label: "My Certificates", icon: Award },
  { id: "profile", label: "My Profile", icon: User },
];

export default function Dashboard() {
  const { user, setUser, timezone } = useApp();
  const [tab, setTab] = useState("courses");
  const [courses, setCourses] = useState([]);
  const [webinars, setWebinars] = useState({ upcoming: [], completed: [] });
  const [orders, setOrders] = useState([]);
  const [certs, setCerts] = useState([]);

  useEffect(() => {
    api.myCourses().then((d) => setCourses(d.courses));
    api.myWebinars().then(setWebinars);
    api.myOrders().then((d) => setOrders(d.orders));
    api.myCertificates().then((d) => setCerts(d.certificates));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 py-12" data-testid="dashboard">
      <div className="asf-overline mb-2">My Learning</div>
      <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Welcome back, {user.name.split(" ")[0]}</h1>

      <div className="grid lg:grid-cols-[220px_1fr] gap-8 mt-8">
        {/* sidebar */}
        <aside className="lg:sticky lg:top-24 h-max">
          <nav className="flex lg:flex-col gap-1 overflow-x-auto">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm whitespace-nowrap transition-colors ${tab === t.id ? "bg-asf-orange/15 text-asf-orange border border-asf-orange/30" : "text-slate-500 hover:bg-slate-100 border border-transparent"}`}>
                  <Icon className="h-4 w-4" /> {t.label}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* content */}
        <div>
          {tab === "courses" && <MyCourses courses={courses} />}
          {tab === "webinars" && <MyWebinars webinars={webinars} timezone={timezone} />}
          {tab === "purchases" && <MyPurchases orders={orders} />}
          {tab === "certificates" && <MyCertificates certs={certs} />}
          {tab === "profile" && <MyProfile user={user} setUser={setUser} />}
        </div>
      </div>
    </div>
  );
}

function MyCourses({ courses }) {
  if (!courses.length) return <Empty title="No courses yet" cta={["Browse courses", "/courses"]} />;
  return (
    <div className="grid sm:grid-cols-2 gap-5" data-testid="my-courses">
      {courses.map((c) => (
        <div key={c.slug} className="asf-card !translate-y-0 hover:!translate-y-0 overflow-hidden">
          <div className="h-32 overflow-hidden"><img src={c.image} alt="" className="h-full w-full object-cover opacity-80" /></div>
          <div className="p-5">
            <div className="asf-overline">{c.industry_name}</div>
            <h3 className="font-display text-lg font-semibold mt-1">{c.title}</h3>
            <div className="mt-3"><Progress value={c.progress} className="h-1.5 bg-slate-200" /></div>
            <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
              <span>{c.progress}% complete</span><span>{c.completed_lessons}/{c.total_lessons} lessons</span>
            </div>
            <Link to={`/learn/${c.slug}`} data-testid={`continue-${c.slug}`} className="btn-primary w-full py-3 mt-4 inline-flex items-center justify-center gap-2">
              <Play className="h-4 w-4" /> {c.progress > 0 ? "Continue" : "Start"}
            </Link>
          </div>
        </div>
      ))}
    </div>
  );
}

function MyWebinars({ webinars, timezone }) {
  const all = webinars.registered || [];
  if (!all.length) return <Empty title="No webinar registrations" cta={["Join a webinar", "/webinars"]} />;
  return (
    <div className="space-y-4" data-testid="my-webinars">
      {all.map((w) => {
        const t = formatWebinarTime(w.start_utc, timezone);
        const past = new Date(w.start_utc) < new Date();
        return (
          <div key={w.id} className="asf-card !translate-y-0 hover:!translate-y-0 p-5 flex items-center gap-4">
            <div className="text-center w-20 shrink-0">
              <div className="font-display text-xl font-bold">{t.time}</div>
              <div className="text-xs text-slate-500">{t.dayLabel}</div>
            </div>
            <div className="flex-1">
              <h3 className="font-medium">{w.webinar_title}</h3>
              <div className="text-xs text-slate-500 mt-1">{w.duration_min} min · Host {w.host}</div>
            </div>
            <span className={`text-xs px-3 py-1 rounded-full ${past ? "bg-slate-200 text-slate-500" : "bg-green-500/15 text-green-400"}`}>
              {past ? "Completed" : "Upcoming"}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function MyPurchases({ orders }) {
  if (!orders.length) return <Empty title="No purchases yet" cta={["Browse courses", "/courses"]} />;
  const downloadInvoice = async (id) => {
    try {
      const blob = await api.invoiceBlob(id);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch { toast.error("Could not open invoice."); }
  };
  return (
    <div className="asf-card !translate-y-0 hover:!translate-y-0 overflow-x-auto" data-testid="my-purchases">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-slate-500 border-b border-slate-200">
          <th className="p-4">Product</th><th className="p-4">Amount</th><th className="p-4 hidden sm:table-cell">Date</th><th className="p-4">Status</th><th className="p-4">Invoice</th>
        </tr></thead>
        <tbody>
          {orders.map((o) => (
            <tr key={o.id} className="border-b border-slate-100">
              <td className="p-4">{o.title}</td>
              <td className="p-4 font-mono">{fmtMoney(o.symbol, o.amount)}</td>
              <td className="p-4 hidden sm:table-cell text-slate-500">{fmtDate(o.paid_at || o.created_at)}</td>
              <td className="p-4"><span className="text-xs px-2.5 py-1 rounded-full bg-green-500/15 text-green-600">Paid</span></td>
              <td className="p-4">
                <button data-testid={`invoice-${o.id}`} onClick={() => downloadInvoice(o.id)} className="text-asf-orange font-semibold inline-flex items-center gap-1 text-xs"><Download className="h-3.5 w-3.5" /> PDF</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MyCertificates({ certs }) {
  if (!certs.length) return <Empty title="No certificates yet" desc="Complete a course to earn your first certificate." cta={["My courses", "/dashboard"]} />;
  return (
    <div className="grid sm:grid-cols-2 gap-5" data-testid="my-certificates">
      {certs.map((c) => (
        <div key={c.certificate_id} className="relative rounded-2xl p-[1px] bg-gradient-to-br from-asf-orange/60 to-blue-500/30">
          <div className="rounded-2xl bg-asf-surface p-6">
            <Award className="h-8 w-8 text-asf-orange mb-3" />
            <div className="asf-overline">Certificate of Completion</div>
            <h3 className="font-display text-lg font-semibold mt-1">{c.course_title}</h3>
            <p className="text-sm text-slate-500 mt-1">{c.student_name} · {c.duration_hours} hours</p>
            <div className="font-mono text-xs text-asf-orange mt-3">{c.certificate_id}</div>
            <div className="flex gap-2 mt-4">
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/certificates/${c.certificate_id}/pdf`} target="_blank" rel="noreferrer" data-testid={`download-cert-${c.certificate_id}`} className="btn-primary px-4 py-2 text-xs inline-flex items-center gap-1"><Download className="h-3.5 w-3.5" /> Download PDF</a>
              <Link to={`/verify/${c.certificate_id}`} className="btn-secondary px-4 py-2 text-xs">Verify</Link>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function MyProfile({ user, setUser }) {
  const [form, setForm] = useState({
    name: user.name || "", phone: user.phone || "", city: user.city || "", timezone: user.timezone || "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const u = await api.updateProfile(form);
      setUser(u);
      toast.success("Profile updated.");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const inp = (key, label) => (
    <div>
      <label className="text-xs text-slate-500">{label}</label>
      <input data-testid={`profile-${key}`} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="w-full rounded-xl bg-asf-app border border-slate-200 px-4 py-3 mt-1 outline-none focus:border-asf-orange" />
    </div>
  );
  return (
    <div className="asf-card !translate-y-0 hover:!translate-y-0 p-6 max-w-lg" data-testid="my-profile">
      <div className="grid sm:grid-cols-2 gap-4">
        {inp("name", "Name")}
        <div><label className="text-xs text-slate-500">Email</label><input disabled value={user.email} className="w-full rounded-xl bg-slate-100 border border-slate-200 px-4 py-3 mt-1 text-slate-500" /></div>
        {inp("phone", "Phone")}
        {inp("city", "City")}
        {inp("timezone", "Timezone")}
        <div><label className="text-xs text-slate-500">Country</label><input disabled value={user.country} className="w-full rounded-xl bg-slate-100 border border-slate-200 px-4 py-3 mt-1 text-slate-500" /></div>
      </div>
      <button data-testid="save-profile" onClick={save} disabled={saving} className="btn-primary px-8 py-3 mt-5">{saving ? "Saving…" : "Save Changes"}</button>
    </div>
  );
}

function Empty({ title, desc, cta }) {
  return (
    <div className="asf-card !translate-y-0 hover:!translate-y-0 p-12 text-center">
      <h3 className="font-display text-xl font-semibold">{title}</h3>
      {desc && <p className="text-slate-500 mt-2">{desc}</p>}
      {cta && <Link to={cta[1]} className="btn-primary inline-block mt-5 px-6 py-3">{cta[0]}</Link>}
    </div>
  );
}
